import { queryClient } from '@/app/providers/react-query-provider';

import { apiAddressBookFetch } from '..';
import { ADDRESS_BOOK_BFF_QUERY_KEYS } from '../address-book-bff/queries';
import { USERS_PROFILE_QUERY_KEYS } from '../users-profile/queries';
import { ADDRESS_BOOK_QUERY_KEYS } from './queries';
import { invalidateAddressBookLists } from './set-query-data';
import type {
  AddContactHistoryEntryPayload,
  BatchSaveCategoryNodePayload,
  ContactFollowUp,
  ContactHistoryEntryDto,
  CreateCategoryNodePayload,
  CreateContactFileUploadRequestPayload,
  CreateContactPayload,
  CreateContactResponse,
  CreateContactUploadRequestPayload,
  CreateContactUploadRequestRes,
  CreateExtractorUploadRequestPayload,
  CreateExtractorUploadRequestRes,
  ExtractContactsPayload,
  ExtractContactsRes,
  IdRes,
  LinkOrganizationPayload,
  MergeCategoryNodesPayload,
  SetContactFollowUpPayload,
  SuccessRes,
  UpdateCategoryNodePayload,
  UpdateContactCategoriesPayload,
  UpdateContactHistoryEntryPayload,
  UpdateContactPayload,
  UpdateOrganizationLinkPayload,
} from './types';

// Every follow-up write changes what the board, the table and the contact card render for this
// user (marker, red highlight, follow-up column), so each one refreshes the same contact reads.
const invalidateContactFollowUp = (contactId: string) => {
  queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
  queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts, contactId] });
};

const invalidateContactHistory = (contactId: string) => {
  queryClient.invalidateQueries({
    queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contactHistory, contactId],
  });
};

// ==================== Contact Mutations ====================

export const _createContact = (body: CreateContactPayload) => {
  return apiAddressBookFetch<CreateContactResponse>('/contacts', {
    method: 'POST',
    body,
    onResponse: () => {
      invalidateAddressBookLists(queryClient);
    },
  });
};

export const _updateContact = ({ id, body }: { id: string; body: UpdateContactPayload }) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${id}`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.ownerAffiliations, id] });
      queryClient.invalidateQueries({ queryKey: [USERS_PROFILE_QUERY_KEYS.profile] });
      invalidateContactHistory(id);
    },
  });
};

export const _updateContactCategories = ({
  id,
  body,
}: {
  id: string;
  body: UpdateContactCategoriesPayload;
}) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${id}/categories`, {
    method: 'PUT',
    body,
    onResponse: () => {
      invalidateAddressBookLists(queryClient);
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts, id] });
      queryClient.invalidateQueries({ queryKey: [USERS_PROFILE_QUERY_KEYS.profile] });
      invalidateContactHistory(id);
    },
  });
};

export const _setContactCategoryValue = ({
  id,
  body,
}: {
  id: string;
  body: { nodeId: string; value: string | null };
}) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${id}/category-values`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts, id] });
      queryClient.invalidateQueries({ queryKey: [USERS_PROFILE_QUERY_KEYS.profile] });
      invalidateContactHistory(id);
    },
  });
};

export const _deleteContact = (id: string) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${id}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.removeQueries({
        queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts, id],
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
    },
  });
};

// ==================== Contact Follow-up Mutations (GP-4203) ====================

export const _setContactFollowUp = ({
  contactId,
  body,
}: {
  contactId: string;
  body: SetContactFollowUpPayload;
}) => {
  return apiAddressBookFetch<ContactFollowUp>(`/contacts/${contactId}/follow-up`, {
    method: 'POST',
    body,
    onResponse: () => invalidateContactFollowUp(contactId),
  });
};

export const _rescheduleContactFollowUp = ({
  contactId,
  body,
}: {
  contactId: string;
  body: SetContactFollowUpPayload;
}) => {
  return apiAddressBookFetch<ContactFollowUp>(`/contacts/${contactId}/follow-up`, {
    method: 'PUT',
    body,
    onResponse: () => invalidateContactFollowUp(contactId),
  });
};

export const _completeContactFollowUp = (contactId: string) => {
  return apiAddressBookFetch<void>(`/contacts/${contactId}/follow-up/complete`, {
    method: 'POST',
    onResponse: () => invalidateContactFollowUp(contactId),
  });
};

export const _removeContactFollowUp = (contactId: string) => {
  return apiAddressBookFetch<void>(`/contacts/${contactId}/follow-up`, {
    method: 'DELETE',
    onResponse: () => invalidateContactFollowUp(contactId),
  });
};

const invalidateOrganizationLinks = () => {
  queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
  queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.linkedContacts] });
  queryClient.invalidateQueries({ queryKey: [USERS_PROFILE_QUERY_KEYS.organizations] });
};

export const _linkOrganization = ({
  contactId,
  body,
}: {
  contactId: string;
  body: LinkOrganizationPayload;
}) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${contactId}/organizations`, {
    method: 'POST',
    body,
    onResponse: invalidateOrganizationLinks,
  });
};

export const _updateOrganizationLink = ({
  contactId,
  organizationId,
  body,
}: {
  contactId: string;
  organizationId: string;
  body: UpdateOrganizationLinkPayload;
}) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${contactId}/organizations/${organizationId}`, {
    method: 'PATCH',
    body,
    onResponse: invalidateOrganizationLinks,
  });
};

export const _unlinkOrganization = ({
  contactId,
  organizationId,
}: {
  contactId: string;
  organizationId: string;
}) => {
  return apiAddressBookFetch<SuccessRes>(`/contacts/${contactId}/organizations/${organizationId}`, {
    method: 'DELETE',
    onResponse: invalidateOrganizationLinks,
  });
};

// ==================== Category Mutations ====================

export const _createCategory = (body: CreateCategoryNodePayload) => {
  return apiAddressBookFetch<IdRes>('/categories', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.categories] });
    },
  });
};

export const _updateCategory = ({ id, body }: { id: string; body: UpdateCategoryNodePayload }) => {
  return apiAddressBookFetch<void>(`/categories/${id}`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.categories] });
    },
  });
};

export const _deleteCategory = (id: string) => {
  return apiAddressBookFetch<void>(`/categories/${id}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.categories] });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
    },
  });
};

export const _batchSaveCategories = (body: BatchSaveCategoryNodePayload[]) => {
  return apiAddressBookFetch<void>('/categories/batch', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.categories] });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
    },
  });
};

export const _mergeCategories = (body: MergeCategoryNodesPayload) => {
  return apiAddressBookFetch<void>('/categories/merge', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_QUERY_KEYS.categories] });
      queryClient.invalidateQueries({ queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts] });
    },
  });
};

// ==================== Contact Upload Mutations ====================

export const _createContactUploadRequest = (body: CreateContactUploadRequestPayload) => {
  return apiAddressBookFetch<CreateContactUploadRequestRes>('/contacts/upload-request', {
    method: 'POST',
    body,
  });
};

// ==================== Contact Extractor Mutations ====================

export const _uploadContactRequest = (body: CreateExtractorUploadRequestPayload) => {
  return apiAddressBookFetch<CreateExtractorUploadRequestRes>('/contact-extractor/upload-request', {
    method: 'POST',
    body,
  });
};

export const _extractContacts = (body: ExtractContactsPayload) => {
  return apiAddressBookFetch<ExtractContactsRes>('/contact-extractor/extract', {
    method: 'POST',
    body,
  });
};

export const _addContactHistoryEntry = ({
  contactId,
  body,
}: {
  contactId: string;
  body: AddContactHistoryEntryPayload;
}) => {
  return apiAddressBookFetch<ContactHistoryEntryDto>(`/contacts/${contactId}/history`, {
    method: 'POST',
    body,
    onResponse: () => invalidateContactHistory(contactId),
  });
};

export const _updateContactHistoryEntry = ({
  contactId,
  entryId,
  body,
}: {
  contactId: string;
  entryId: string;
  body: UpdateContactHistoryEntryPayload;
}) => {
  return apiAddressBookFetch<ContactHistoryEntryDto>(`/contacts/${contactId}/history/${entryId}`, {
    method: 'PATCH',
    body,
    onResponse: () => invalidateContactHistory(contactId),
  });
};

export const _createContactFileUploadRequest = ({
  contactId,
  body,
}: {
  contactId: string;
  body: CreateContactFileUploadRequestPayload;
}) => {
  return apiAddressBookFetch<CreateContactUploadRequestRes>(
    `/contacts/${contactId}/files/upload-request`,
    {
      method: 'POST',
      body,
    },
  );
};
